import { AttributionDetails, AttributionDetailsForChannelTesting } from '../components/ApplicationForm/types'
import { affiliateSources, digitalSources, dualBrandedSourcesNotPrefilled } from './utils-sources'
import { zaPrefillAttributeMissing } from './monitoring-utils/monitoring-utils'

export enum Channels {
  DIGITAL = 'digital',
  CRM = 'crm',
  CLASSIFIED = 'classified',
  AFFILIATE = 'affiliate'
}

export type ResultOption = {
  [key: string]: string
  label: string
  value: string
}

export function isDualBrandedSourceNotPrefilled(source: string) {
  return dualBrandedSourcesNotPrefilled.includes(source)
}

// The below sources and mediums should never be duplicated across channels
// Please be careful when adding to this
export const returnChannelName = ({ source, medium }: AttributionDetails | AttributionDetailsForChannelTesting): Channels | null => {
  if (source === 'autotrader') return Channels.CLASSIFIED

  if (affiliateSources.includes(source)) return Channels.AFFILIATE

  if (digitalSources.includes(source) || medium === 'organic' || medium === 'cpc' || medium === 'social' || medium === 'myzuto') return Channels.DIGITAL

  if (medium === 'email' || medium === 'sms' || medium === 'crm') return Channels.CRM
  return null
}

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  return String(error)
}

/**
 * Sorts the returned address list numberically, and alphabetically, in the following priority:
 * - Ordered by number and alphabetically e.g. 1, 2, 2A, 2B
 * - If address list contains a house name and number, e.g. 'Kings Place, 2B, High Road' - then it will be sorted by number first, then letter
 * - If the address contains only a house name (without a number), it will appear at the end of the list
 *
 * NOTE: This should work in the vast majority of cases, however we can't guarantee there won't
 * be any unhandled edge cases due to the limitation of the data format.
 */
export const sortAddressList = <T extends { label: string }>(address: T[]) => address.sort((a, b) => {
  // Remove the postcode from the string for sorting to avoid any confusion with house number. (This will always be everything before the last , in the string)
  const firstPartOfLabelA: string = a.label.substring(0, a.label.lastIndexOf(','))
  const firstPartOfLabelB: string = b.label.substring(0, b.label.lastIndexOf(','))

  // Extracting the numeric part of the address label for both `a` and `b`
  const numberA = firstPartOfLabelA.match(/\d+/g)
  const numberB = firstPartOfLabelB.match(/\d+/g)

  // Extracting the alphabetic part of the address label for both `a` and `b`
  const letterA = firstPartOfLabelA.match(/\d+([A-Za-z]+)/g)
  const letterB = firstPartOfLabelB.match(/\d+([A-Za-z]+)/g)

  // Check if both `a` and `b` don't have numeric parts
  // If yes, return comparison result of address labels using `localeCompare
  if (!numberA && !numberB) {
    return firstPartOfLabelA.localeCompare(firstPartOfLabelB)
  }
  // Check if only `a` has numeric part
  // If yes, return 1 to sort `b` before `a`
  if (!numberA) {
    return 1
  }
  // Check if only `b` has numeric part
  // If yes, return -1 to sort `a` before `b`
  if (!numberB) {
    return -1
  }

  // Parsing the numeric parts of `a` and `b` as integers
  const parsedNumberA = parseInt(numberA[0], 10)
  const parsedNumberB = parseInt(numberB[0], 10)

  // Compare the parsed numeric parts of `a` and `b`:
  // If `a` is less than `b`, return -1 to sort `a` before `b`
  if (parsedNumberA < parsedNumberB) {
    return -1
  }
  // If `a` is greater than `b`, return 1 to sort `b` before `a`
  if (parsedNumberA > parsedNumberB) {
    return 1
  }

  // Check if both `a` and `b` don't have alphabetic parts
  // If yes, return 0 as they are equal
  if (!letterA && !letterB) {
    return 0
  }

  // Check if only `a` has alphabetic part
  // If yes, return -1 to sort `a` before `b`
  if (!letterA) {
    return -1
  }
  // Check if only `b` has alphabetic part
  // If yes, return 1 to sort `b` before `a`
  if (!letterB) {
    return 1
  }

  // Compare the alphabetic parts of `a` and `b`:
  // If `a` is less than `b`, return -1 to sort `a` before `b`
  if (letterA[0] < letterB[0]) {
    return -1
  }
  // If a is greater than b, return 1 to sort b before a
  if (letterA[0] > letterB[0]) {
    return 1
  }

  // If a and b are equal, return 0
  return 0
})

/**
 * - Filters values from an array (of objects) by comparing it to a given string value
 * - Compares the given string with all string values within each object in the array
 */
export const filterByValue = (array: ResultOption[], string: string) => array.filter((item) => Object.keys(item).some((k) => item[k].replace(/\s/g, '').toLowerCase().includes(string.replace(/\s/g, '').toLowerCase())))

/**
 * Returns a suffix 's' if the given number is greater than 1.
 *
 * @param {string} word - The word to add the plural suffix to.
 * @param {number} number - The number to determine whether to add a plural suffix.
 * @returns The word (string) with the plural suffix ('s' if the number is greater than 1, empty string otherwise).
 */
export const pluralize = (word: string, number: number) => `${word}${number > 1 ? 's' : ''}`

/**
 * Converts a string to kebab case by replacing spaces with hyphens and converting to lowercase.
 *
 * @param {string} title - The string to be converted.
 * @returns The kebab-cased string.
 */
export const convertToKebabCase = (str: string) => {
  return str
    .replace(/[^\w\s]/gi, ' ') // Replace non-alphanumeric characters for spaces
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .toLowerCase()
}

/**
 * Checks if a string contains only the '£' character.
 * @param {string} str - The string to be tested.
 * @returns - Returns true if the string contains only '£', false otherwise.
 */
export const containsOnlyPoundSymbol = (str: string) => /^£+$/.test(str)

/**
 * Calculates the total duration in months based on the provided duration object.
 * @param {Object} duration - The object containing the duration for each key value.
 * @returns - The total duration (number) in months.
 */
export const calculateTotalMonths = (duration: object) => {
  const sum = Object.values(duration).reduce((acc, val) => {
    const years = Math.max(val.years || 0, 0)
    const months = Math.max(val.months || 0, 0)
    const totalMonths = years * 12 + months
    return acc + totalMonths
  }, 0)

  return sum
}

export default returnChannelName

/**
 * Update display text based on formatted string
 * @param formattedString The formatted string to check
 * @returns The updated display text if needed, otherwise returns the original formatted string
 */
export const updateLenderNameDisplayText = (formattedString: string): string => {
  if (formattedString === 'Smf') {
    return 'Specialist Motor Finance'
  }

  return formattedString
}

/**
 * @param inputString The string to be converted
 * @returns The word with Capitalised first letter with lowercase letters following
 */
export const sanitizeLenderName = (inputString: string): string => {
  const words = inputString.split(' ')

  // Capitalize the first letter of each word and lowercase the rest, while removing symbols
  const formattedWords = words.map(word => {
    const cleanedWord = word.replace(/[^a-zA-Z0-9]/g, '') // Remove non-alphanumeric characters
    return cleanedWord.charAt(0).toUpperCase() + cleanedWord.slice(1).toLowerCase()
  })

  // Join the formatted words back into a string
  const formattedString = formattedWords.join(' ')

  const updatedLenderNameDisplayText = updateLenderNameDisplayText(formattedString)

  return updatedLenderNameDisplayText
}

export const isBrandPaidSearch = (medium: string, campaign: string) => {
  return (medium === 'cpc') && campaign.toLowerCase().includes('brand')
}

/**
 * Checks attribution of source and medium to confirm if it's social channel.
 * @param {object} attribution - Object containing attribution details - source, medium, campaign etc.
 * @returns {boolean} Returns true if medium is social. 
 */
export const isSocial = (attribution: AttributionDetails) => {
  return returnChannelName(attribution) === 'digital' && attribution?.medium === 'social'
}

/**
 * Checks attribution including utm_content to confirm if the social interstitial should be shown.
 * @param {object} attribution - Object containing attribution details - source, medium, campaign, content etc.
 * @returns {boolean} Returns true if the interstitial can be shown.
 */
export const isEligibleForFirstTimeApplicantPreInterstitialV1 = (attribution: AttributionDetails) => {
  if (isSocial(attribution)) {
    return attribution?.content?.toLowerCase().includes('luke')
  }
  return false
}

/**
 * Calculate category based on date of birth.
 * @param {object} dateOfBirth - Object containing day, month, and year of birth.
 * @param {string} dateOfBirth.day - The day of birth.
 * @param {string} dateOfBirth.month - The month of birth.
 * @param {string} dateOfBirth.year - The year of birth.
 * @returns {string} Returns 'A' if age is 35 or under, and 'B' if age is 36 or older.
 */
export const calculateDOBCategory = (dateOfBirth: { day: string; month: string; year: string }): string => {
  const { day, month, year } = dateOfBirth

  const birthDate: Date = new Date(`${year}-${month}-${day}`)
  const currentDate: Date = new Date()

  let age: number = currentDate.getFullYear() - birthDate.getFullYear()

  if (
    currentDate.getMonth() < birthDate.getMonth() ||
    (currentDate.getMonth() === birthDate.getMonth() && currentDate.getDate() < birthDate.getDate())
  ) {
    age--
  }

  return age <= 35 ? 'A' : 'B'
}


export const isNullOrEmpty = (value: any, attribute: string, source: string): boolean => {
  const res = value == null || value === ''
  if (res) zaPrefillAttributeMissing({ source, attribute, value, origin: 'isNullOrEmpty' })
  return res
}